import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LinkComponent } from '../../atoms/link/link.component';
import { ButtonComponent } from '../../molecules/button/button.component';
import { TeaserComponent } from '../../molecules/teaser/teaser.component';
import { Link } from '../../../shared/interfaces/link.interface';
import LinkType from '../../../shared/enums/link-type.enums';
import { ButtonType } from '../../../shared/enums/button-type.enums';
import { CaasComponents } from '../../../shared/interfaces/caas/caas.interface';
import { Observable, Subject, takeUntil } from 'rxjs';
import { HrefType } from '../../../shared/enums/href-type.enums';
import { Teaser } from '../../../shared/interfaces/teaser.interface';
import { ManipulateDataService } from '../../../services/translate.service';
import { CaasRoutesService } from '../../../services/caas-routes.service';
import { Router } from '@angular/router';
import { CapitalizePipe } from '../../capitalize.pipe';
import TeaserType from '../../../shared/enums/teaser.type.enums';
import { News } from '../../../shared/interfaces/news.interface';

@Component({
  selector: 'hdi-teaser-container',
  standalone: true,
  imports: [
    CommonModule,
    LinkComponent,
    ButtonComponent,
    TeaserComponent,
    CapitalizePipe,
  ],
  templateUrl: './teaser-container.component.html',
  styleUrls: ['./teaser-container.component.scss'],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class TeaserContainerComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild('teaserList') teaserList!: ElementRef;
  @Input() caasData?: CaasComponents['value'][number];
  @Input() caasNewsData?: CaasComponents['value'][number];
  @Input() columns?: number;
  @Input() imageOrientation?: string;
  @Input() linkHeadline?: string;
  @Input() link?: Link;
  @Input() showMoreLess: boolean = false;
  @Input() initialVisibleTeasers: number = 4;
  @Input() newsTeasers$?: Observable<Teaser[]>;
  @Input() type?: 'standard' | 'news';

  public readonly ButtonType = ButtonType;
  public readonly LinkType = LinkType;
  public readonly HrefType = HrefType;
  public teasers: ElementRef[] = [];
  public backupTeasers: ElementRef[] = [];
  public visible_teasers: number;
  private teaserStep: number = 4;
  private destroy = new Subject<void>();
  TeaserType = TeaserType;

  constructor(
    private renderer: Renderer2,
    private manipulateDataService: ManipulateDataService,
    private caasRoutesService: CaasRoutesService,
    private router: Router
  ) {
    this.visible_teasers = this.initialVisibleTeasers;
  }

  ngAfterViewInit(): void {
    this.setupVisibility();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData']) {
      this.setCaasContent(changes['caasData'].currentValue);
      // Determine type based on template UID
      if (!this.type) {
        this.type =
          this.caasData?.template.uid.toString() === 'st_teasers'
            ? 'standard'
            : 'news';
      }
    }
    if (changes['caasNewsData']) {
      this.setCaasNews(changes['caasNewsData'].currentValue);
    }
  }

  get showMoreLessLabel(): string {
    return this.visible_teasers >= this.teasers.length
      ? 'Weniger anzeigen'
      : 'Mehr anzeigen';
  }

  get iconSelected(): string {
    return this.visible_teasers >= this.teasers.length ? 'minus' : 'plus';
  }

  setupVisibility() {
    if (this.showMoreLess) {
      this.initializeVisibleTeasers();
    }
  }

  /**
   * Initialize the visible teasers based or not on the filtered news.
   * @param filteredNews - The filtered news is specific for teaser news. It is defined only when a filter is applied.
   * @param backupTeasers - The first time news are rendered (no filter applied) a template teaser backup is saved.
   * Every time a filter is applied, initializeVisibleTeasers is triggered.
   */
  initializeVisibleTeasers(filteredNews?: News[]) {
    if (filteredNews) {
      this.teasers = Array.from(this.backupTeasers).filter((teaser) =>
        filteredNews
          .slice(3)
          .some(
            (news) => news.identifier === (teaser as unknown as HTMLElement).id
          )
      );
    } else {
      this.teasers = Array.from(this.teaserList?.nativeElement?.children ?? []);
      this.backupTeasers = this.teasers;
    }
    switch (this.columns) {
      case 3:
        this.initialVisibleTeasers = 6;
        this.visible_teasers = 6;
        this.teaserStep = 6;
        break;
      case 2:
        this.initialVisibleTeasers = 4;
        this.visible_teasers = 4;
        this.teaserStep = 4;
        break;
      case 0:
        this.visible_teasers = this.initialVisibleTeasers;
        this.teaserStep = this.initialVisibleTeasers;
        break;
      default:
        this.visible_teasers = 4;
        break;
    }

    this.teasers.forEach((teaser, index) => {
      if (index >= this.visible_teasers) {
        this.renderer.setStyle(teaser, 'display', 'none');
      } else {
        this.renderer.setStyle(teaser, 'display', 'block');
      }
    });
    this.updateShowMoreLess(this.teasers.length, this.visible_teasers);
  }

  toggleTeasersVisibility() {
    if (this.visible_teasers < this.teasers.length) {
      this.visible_teasers += this.teaserStep;
      this.teasers?.forEach((teaser, index) => {
        if (index < this.visible_teasers) {
          this.renderer.setStyle(teaser, 'display', 'block');
        }
      });
    } else {
      this.visible_teasers = this.initialVisibleTeasers;
      this.teasers?.forEach((teaser, index) => {
        if (index >= this.visible_teasers) {
          this.renderer.setStyle(teaser, 'display', 'none');
        }
      });
    }
  }

  setCaasContent(caasData: CaasComponents['value'][number]) {
    this.columns = this.setCaasColumns(caasData);
    this.imageOrientation =
      caasData.formData.st_image_orientation?.value?.label;
    this.showMoreLess = this.setCaasShowMoreLess(caasData);
  }

  setCaasColumns(caasData: CaasComponents['value'][number]): number {
    switch (caasData.formData.st_layout!.value.identifier) {
      case 'horizontal':
        switch (caasData.formData.st_columns_horizontal!.value.identifier) {
          case '1':
            return 1;
          default:
            return 2;
        }
      default:
        switch (caasData.formData.st_columns_standard!.value.identifier) {
          case '2':
            return 2;
          default:
            return 3;
        }
    }
  }

  setCaasShowMoreLess(caasData: CaasComponents['value'][number]): boolean {
    return !!caasData.formData.st_more_button?.value;
  }

  updateShowMoreLess(teasersLength: number, visibleTeasers: number) {
    if (teasersLength <= visibleTeasers) {
      this.showMoreLess = false;
    }
  }

  setCaasNews(caasNewsData: CaasComponents['value'][number]) {
    let linkUrl = '';

    // Set Link Headline
    if (caasNewsData?.formData?.st_header?.value) {
      this.linkHeadline = caasNewsData.formData.st_header.value;
    }

    // Process Link
    if (caasNewsData?.formData?.st_link?.value) {
      const internalLink =
        caasNewsData.formData.st_link.value.formData?.lt_link_internal?.value;

      if (internalLink && internalLink.identifier) {
        linkUrl =
          this.caasRoutesService.getSeoRouteByIdentifier(
            internalLink.identifier,
            this.router.config
          )?.path ?? '/404';
      } else if (
        caasNewsData.formData.st_link.value.formData?.lt_link_external?.value
      ) {
        linkUrl =
          caasNewsData.formData.st_link.value.formData.lt_link_external.value;
      }

      this.link = {
        label:
          caasNewsData.formData.st_link.value.formData?.lt_link_label?.value ??
          'Mehr erfahren',
        href: linkUrl,
        hrefType: internalLink ? HrefType.ANGULAR_ROUTE : HrefType.EXTERNAL,
        title:
          caasNewsData.formData.st_link.value.formData?.lt_link_title?.value ??
          'Mehr erfahren',
      };
    }

    // Fetch News Teasers
    if (
      caasNewsData?.formData?.st_selection_type?.value?.identifier ===
      'automatic'
    ) {
      const filterId =
        caasNewsData?.formData?.st_filter?.value?.target?.identifier ||
        'latest';
      const quantity =
        caasNewsData?.formData?.st_quantity?.value?.identifier ?? '3';

      this.newsTeasers$ = this.manipulateDataService
        .getTeasersNewsByFilterId(filterId, quantity)
        .pipe(takeUntil(this.destroy));

      // Optionally, subscribe to verify
      this.newsTeasers$.subscribe({
        error: (error) =>
          console.error(
            'TeaserContainer > newsTeasers$$ (automatic) subscription error:',
            error
          ),
      });
    }

    if (
      caasNewsData?.formData?.st_items?.value.length &&
      caasNewsData?.formData?.st_selection_type?.value?.identifier === 'manual'
    ) {
      this.newsTeasers$ = this.manipulateDataService
        .getTeasersNewsByChoice(caasNewsData.formData.st_items.value)
        .pipe(takeUntil(this.destroy));

      // Optionally, subscribe to verify
      this.newsTeasers$.subscribe({
        error: (error) =>
          console.error(
            'TeaserContainer > newsTeasers$ (manual) subscription error:',
            error
          ),
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }
}
